import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { PageHeader } from "@/components/ui/PageHeader";
import { TabSelector } from "@/components/TabSelector";
import { useReferrals } from "@/providers/ReferralsProvider";
import { useUserEvents, useUserStats } from "@/hooks/useLeaderboard";
import {
  HowItWorksCard,
  LeaderboardTable,
  MissionsList,
  ReferralCodeCard,
  ReferralStatCards,
} from "@/components/referrals/ReferralSections";

type ReferralTab = "missions" | "leaderboard";

/** Mobile Referrals: code card, stats, Missions / Leaderboard tabs, How It Works. */
export const ReferralsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const tgUser = useLaunchParams().initData?.user;
  const userId = tgUser?.id?.toString();
  const { data, user, isWhitelisted, handleCopyCode, handleShareCode } =
    useReferrals();
  const { data: events } = useUserEvents(userId);
  const { data: userStats } = useUserStats(userId);
  const [tab, setTab] = useState<ReferralTab>("missions");

  if (!data || !user || !isWhitelisted) {
    return null;
  }

  const userPoints = userStats?.totalPoints ?? 0;
  const missions = [...(events ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 pb-28 [&>*]:shrink-0">
        <PageHeader
          title="Invite. Trade."
          accent="Earn Together."
          subtitle="Bring your friends to Trigger and earn points as they trade. The more you refer, the more you earn."
        />
        <ReferralCodeCard
          referralCode={data.referralStats.referralCode}
          codeNeedsRefresh={data.referralStats.codeNeedsRefresh}
          canUseCode={data.referralStats.canUseCode}
          onCopyCode={handleCopyCode}
          onShareCode={handleShareCode}
          onGenerateNewCode={() => navigate("/generate-new-code")}
        />
        <ReferralStatCards
          mobile
          referrals={data.referralStats.totalReferrals}
          points={userPoints}
          multiplier={userStats?.multiplier ? userStats.multiplier / 100 : 1}
        />
        <div className="border-t border-white/10" />
        <TabSelector
          options={[
            { value: "missions", label: "Missions" },
            { value: "leaderboard", label: "Leaderboard" },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === "missions" ? (
          <section className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold leading-7 text-white">Level up your Trading</h3>
              <p className="text-sm leading-5 text-[#a3a3a3]">
                Complete referral and trading milestones to earn more points and increase your multiplier.
              </p>
            </div>
            <MissionsList events={missions} mobile />
          </section>
        ) : (
          <section className="glass overflow-hidden !rounded-2xl">
            <LeaderboardTable
              compact
              leaderboard={data.leaderboard}
              you={{
                name: tgUser?.username || tgUser?.firstName || "You",
                rank: data.referralStats.userRank,
                points: userPoints,
              }}
            />
          </section>
        )}
        <HowItWorksCard />
      </div>
      <ReferralBottomNav />
    </div>
  );
};
